import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { BellRing, BellOff, Loader2, Save, Mail } from "lucide-react";
import { toast } from "sonner";

const STATES = [
  { key: "on", label: "On" },
  { key: "weekly", label: "Weekly summary" },
  { key: "off", label: "Off" },
];

export const NotificationPreferences = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pausedAll, setPausedAll] = useState(false);
  const [catalogue, setCatalogue] = useState([]);
  const [settings, setSettings] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/email-preferences/me");
      setPausedAll(!!data.paused_all);
      setCatalogue(data.catalogue || []);
      setSettings(data.settings || {});
    } catch (e) {
      toast.error("Could not load notification preferences");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const setCat = (key, value) => setSettings((s) => ({ ...s, [key]: value }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/email-preferences/me", {
        paused_all: pausedAll,
        categories: settings,
      });
      setPausedAll(!!data.paused_all);
      setSettings(data.settings || {});
      toast.success("Notification preferences saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  if (loading) {
    return (
      <div className="bg-white border border-slate-200 rounded-lg p-6 flex items-center gap-2 text-slate-500 text-sm"
           data-testid="notif-prefs-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading notification preferences…
      </div>
    );
  }

  return (
    <div id="notifications" className="bg-white border border-slate-200 rounded-lg p-6" data-testid="notif-prefs-card">
      <div className="flex items-center gap-2 mb-1">
        <Mail className="w-4 h-4 text-slate-500" />
        <h2 className="text-base font-semibold text-slate-900">Email notifications</h2>
      </div>
      <p className="text-sm text-slate-500 mb-5">
        Choose which Hub emails you receive and how often. Invitations, password resets and
        security codes are always sent.
      </p>

      {/* Master pause */}
      <div className={`flex items-start gap-3 p-4 rounded-lg border mb-5 ${pausedAll ? "border-amber-200 bg-amber-50/60" : "border-slate-200 bg-slate-50/60"}`}>
        {pausedAll ? <BellOff className="w-5 h-5 text-amber-700 shrink-0 mt-0.5" /> : <BellRing className="w-5 h-5 text-slate-500 shrink-0 mt-0.5" />}
        <div className="flex-1">
          <div className="text-sm font-medium text-slate-900">Pause all non-essential emails</div>
          <div className="text-xs text-slate-500">Turns off every category below at once. Individual settings are kept for when you resume.</div>
        </div>
        <button
          data-testid="notif-pause-all-toggle"
          onClick={() => setPausedAll((p) => !p)}
          className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${pausedAll ? "bg-amber-500" : "bg-slate-300"}`}>
          <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${pausedAll ? "translate-x-5" : "translate-x-1"}`} />
        </button>
      </div>

      {/* Categories */}
      <div className={`space-y-3 ${pausedAll ? "opacity-50 pointer-events-none" : ""}`}>
        {catalogue.map((c) => (
          <div key={c.key} data-testid={`notif-cat-${c.key}`}
               className="flex items-center gap-4 py-2 border-b border-slate-100 last:border-0">
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-slate-800">{c.label}</div>
              <div className="text-xs text-slate-500">{c.description}</div>
            </div>
            <div className="flex rounded-lg border border-slate-200 overflow-hidden shrink-0">
              {STATES.map((st) => {
                const active = (settings[c.key] || c.default) === st.key;
                return (
                  <button key={st.key}
                    data-testid={`notif-${c.key}-${st.key}`}
                    onClick={() => setCat(c.key, st.key)}
                    className={`text-xs px-3 py-1.5 transition-colors ${
                      active
                        ? st.key === "off" ? "bg-slate-900 text-white"
                          : st.key === "weekly" ? "bg-blue-600 text-white" : "bg-emerald-600 text-white"
                        : "bg-white text-slate-500 hover:bg-slate-50"}`}>
                    {st.label}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-6 flex justify-end">
        <Button onClick={save} disabled={saving} data-testid="notif-prefs-save-btn">
          {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Save className="w-4 h-4 mr-1.5" />}
          Save preferences
        </Button>
      </div>
    </div>
  );
};
